import { DEFAULT_CONFIG } from '../config/constants';
import { ACCESSIBILITY_OPTIONS } from '../config/options';
import StateManager from './StateManager';

// Feature Imports
import BionicReading from '../features/bionic-reading';
import ContentChunking from '../features/content-chunking';
import Magnifier from '../features/magnifier';
import ReadingPacer from '../features/reading-pacer';
import ReadingWindow from '../features/reading-window';
import SemanticOverlay from '../features/semantic-overlay';
import FocusPath from '../features/focus-path';
import AutoScroll from '../features/auto-scroll';
import TutorialManager from './TutorialManager';
import SoundFeedback from '../features/sound-feedback';
import TextToSpeech from '../features/text-to-speech';
import AiVision from '../features/ai-vision';
import TextSimplifier from '../features/text-simplifier';
import ColorFilters from '../features/color-filters';
import SmartNavigation from '../features/smart-navigation';

export default class AccessibilitySidebar {
  constructor(config = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.state = StateManager.load();
    
    this.init();
  }

  init() {
    this.sidebar = document.querySelector(this.config.sidebarSelector);
    if (!this.sidebar) return;

    // Move sidebar to documentElement to avoid filter/transform scope issues on body
    // This fixes the issue where "Invert Colors" (filter on body) breaks position:fixed scroll
    if (this.sidebar.parentElement !== document.documentElement) {
      document.documentElement.appendChild(this.sidebar);
    }

    this.cacheElements();
    this.initFeatures();
    this.bindEvents();
    this.applyState();
    this.setupFocusableElements();
  }

  cacheElements() {
    this.triggers = Array.from(this.sidebar.querySelectorAll(this.config.triggerSelector));
    this.primaryTrigger = this.triggers[0] || null;
    this.panel = this.sidebar.querySelector(this.config.panelSelector);
    this.closeBtn = this.sidebar.querySelector(this.config.closeSelector);
    this.resetBtn = this.sidebar.querySelector(this.config.resetSelector);
    this.readingRuler = document.querySelector(this.config.rulerSelector);
  }

  initFeatures() {
    // Instantiate all complex features
    this.features = {
      bionicReading: new BionicReading(),
      readingPacer: new ReadingPacer(),
      readingWindow: new ReadingWindow(),
      focusPath: new FocusPath(),
      autoScroll: new AutoScroll(),
      soundFeedback: new SoundFeedback(this.sidebar),
      magnifier: new Magnifier(),
      textToSpeech: new TextToSpeech(this.sidebar),
      semanticOverlay: new SemanticOverlay(this.sidebar),
      aiVision: new AiVision(),
      textSimplifier: new TextSimplifier(),
      smartNavigation: new SmartNavigation(),
    };
    
    this.tutorialManager = new TutorialManager();

    // Dependencies
    this.features.contentChunking = new ContentChunking(this.features.bionicReading);
  }

  bindEvents() {
    this.triggers.forEach((trigger) => {
      trigger.addEventListener('click', () => this.togglePanel());
    });
    this.closeBtn?.addEventListener('click', () => this.closePanel());
    this.resetBtn?.addEventListener('click', () => this.resetState());

    document.addEventListener('keydown', (e) => this.handleKeyDown(e));

    this.sidebar.querySelectorAll(this.config.accordionHeaderSelector).forEach((header) => {
      header.addEventListener('click', () => this.toggleAccordion(header));
    });

    this.sidebar.querySelectorAll(this.config.optionBtnSelector).forEach((btn) => {
      btn.addEventListener('click', () => this.handleOptionClick(btn));
    });

    // Global handlers that were in the original class but logically belong to features or specific toggles
    // Reading Ruler Tracking
    document.addEventListener('mousemove', (e) => {
      if (this.readingRuler && document.documentElement.classList.contains('a11y-reading-ruler')) {
        this.readingRuler.style.top = `${e.clientY}px`;
      }
    });
  }

  // --- Interaction Handlers ---

  togglePanel() {
    this.state.panelOpen ? this.closePanel() : this.openPanel();
  }

  openPanel() {
    this.panel.classList.add(this.config.openClass);
    this.triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'true'));
    this.state.panelOpen = true;
    StateManager.save(this.state);
    this.setupFocusableElements();
    this.focusableElements[0]?.focus();
  }

  closePanel() {
    this.panel.classList.remove(this.config.openClass);
    this.triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    this.state.panelOpen = false;
    StateManager.save(this.state);
    this.primaryTrigger?.focus();
  }

  toggleAccordion(header) {
    const accordion = header.closest(this.config.accordionSelector);
    const section = accordion.dataset.section;
    const isExpanded = accordion.classList.contains(this.config.expandedClass);

    accordion.classList.toggle(this.config.expandedClass);
    header.setAttribute('aria-expanded', !isExpanded);

    if (isExpanded) {
      this.state.expandedSections = this.state.expandedSections.filter((s) => s !== section);
    } else if (!this.state.expandedSections.includes(section)) {
      this.state.expandedSections.push(section);
    }
    StateManager.save(this.state);
  }

  handleOptionClick(btn) {
    const { category, option } = btn.dataset;
    const stateKey = `${category}.${option}`;
    const optionConfig = ACCESSIBILITY_OPTIONS[category]?.[option];
    if (!optionConfig) return;

    if (optionConfig.toggle) this.toggleOption(stateKey, optionConfig);
    else if (optionConfig.levels) this.cycleOption(stateKey, optionConfig);

    if (window.matchMedia('(max-width: 768px)').matches) {
      this.closePanel();
    }
  }

  toggleOption(stateKey, optionConfig) {
    const newValue = !this.state.options[stateKey];
    this.state.options[stateKey] = newValue;
    document.documentElement.classList.toggle(optionConfig.class, newValue);
    this.updateButtonState(stateKey, newValue);
    this.applyOptionEffects(stateKey, newValue);
    StateManager.save(this.state);
  }

  cycleOption(stateKey, optionConfig) {
    const currentLevel = this.state.options[stateKey] || 0;
    const newLevel = currentLevel >= optionConfig.levels ? 0 : currentLevel + 1;

    optionConfig.classes.forEach((c) => document.documentElement.classList.remove(c));
    if (newLevel > 0) document.documentElement.classList.add(optionConfig.classes[newLevel - 1]);

    this.state.options[stateKey] = newLevel;
    this.updateButtonLevel(stateKey, newLevel);
    this.applyOptionEffects(stateKey, newLevel);
    StateManager.save(this.state);
  }

  // --- UI Updates ---

  updateButtonState(stateKey, isActive) {
    const [cat, opt] = stateKey.split('.');
    const btn = this.sidebar.querySelector(`[data-category="${cat}"][data-option="${opt}"]`);
    btn?.classList.toggle(this.config.activeClass, isActive);
  }

  updateButtonLevel(stateKey, level) {
    const [cat, opt] = stateKey.split('.');
    const btn = this.sidebar.querySelector(`[data-category="${cat}"][data-option="${opt}"]`);
    if (!btn) return;
    btn.classList.toggle(this.config.activeClass, level > 0);
    btn.querySelectorAll('.a11y-option__dot').forEach((d, i) => d.classList.toggle(this.config.activeClass, i < level));
  }

  // --- State Application ---

  applyState() {
    if (this.state.panelOpen) { 
        this.openPanel();
    } // Ensure panel state matches

    Object.entries(ACCESSIBILITY_OPTIONS).forEach(([category, options]) => {
      Object.entries(options).forEach(([optionKey, optionConfig]) => {
        const stateKey = `${category}.${optionKey}`;
        const currentValue = this.state.options[stateKey];

        if (optionConfig.toggle) {
          document.documentElement.classList.toggle(optionConfig.class, currentValue === true);
          this.updateButtonState(stateKey, currentValue === true);
          this.applyOptionEffects(stateKey, currentValue === true);
        } else if (optionConfig.levels) {
          optionConfig.classes.forEach((classes) => document.documentElement.classList.remove(classes));
          if (currentValue > 0 && currentValue <= optionConfig.levels) {
            document.documentElement.classList.add(optionConfig.classes[currentValue - 1]);
          }
          this.updateButtonLevel(stateKey, currentValue || 0);
          this.applyOptionEffects(stateKey, currentValue || 0);
        }
      });
    });

    this.state.expandedSections.forEach((section) => {
      const accordion = this.sidebar.querySelector(`[data-section="${section}"]`);
      accordion?.classList.add(this.config.expandedClass);
    });
  }

  applyOptionEffects(stateKey, value) {
    const [, option] = stateKey.split('.');
    const { features } = this;

    // Trigger specific tutorial if feature is being enabled
    // value can be boolean (true) or number (level > 0)
    if (value) {
        this.tutorialManager.show(option);
    }

    switch (option) {
      case 'bionicReading':
        value ? features.bionicReading.enable() : features.bionicReading.disable();
        break;
      case 'magnifier':
        value ? features.magnifier.enable() : features.magnifier.disable();
        break;
      case 'readingPacer':
        features.readingPacer.setSpeed(value);
        break;
      case 'contentChunking':
        value ? features.contentChunking.enable() : features.contentChunking.disable();
        break;
      case 'virtualReadingWindow':
        value ? features.readingWindow.enable() : features.readingWindow.disable();
        break;
      case 'semanticOverlay':
        value ? features.semanticOverlay.enable() : features.semanticOverlay.disable();
        break;
      case 'focusPath':
        value ? features.focusPath.enable() : features.focusPath.disable();
        break;
      case 'autoScroll':
        features.autoScroll.setSpeed(value);
        break;
      case 'soundFeedback':
        value ? features.soundFeedback.enable() : features.soundFeedback.disable();
        break;
      case 'textToSpeech':
        value ? features.textToSpeech.enable() : features.textToSpeech.disable();
        if (value) this.tutorialManager.show('textToSpeech');
        break;
      case 'aiVision':
        value ? features.aiVision.enable() : features.aiVision.disable();
        if (value) this.tutorialManager.show('aiVision');
        break;
      case 'textSimplifier':
        value ? features.textSimplifier.enable() : features.textSimplifier.disable();
        if (value) this.tutorialManager.show('textSimplifier');
        break;
      case 'smartNavigation':
        value ? features.smartNavigation.enable() : features.smartNavigation.disable();
        if (value) this.tutorialManager.show('smartNavigation');
        break;
      case 'colorBlindness':
        if (value > 0) ColorFilters.ensure();
        break;
      default:
        break;
    }
  }

  resetState() {
    this.state = StateManager.reset();
    StateManager.save(this.state);

    // Clear UI classes
    const html = document.documentElement;
    Object.values(ACCESSIBILITY_OPTIONS).forEach((options) => {
      Object.values(options).forEach((cfg) => {
        if (cfg.toggle) html.classList.remove(cfg.class);
        else if (cfg.classes) cfg.classes.forEach((c) => html.classList.remove(c));
      });
    });

    // Reset Buttons
    this.sidebar.querySelectorAll(this.config.optionBtnSelector).forEach((btn) => {
      btn.classList.remove(this.config.activeClass);
      btn.querySelectorAll('.a11y-option__dot').forEach((d) => d.classList.remove(this.config.activeClass));
    });

    // Disable all features
    Object.values(this.features).forEach(feature => {
        if (feature.disable) feature.disable();
        if (feature.setSpeed) feature.setSpeed(0);
    });
  }

  // --- Accessibility Helpers ---

  handleKeyDown(e) {
    if (e.ctrlKey && e.key.toLowerCase() === 'u') {
      e.preventDefault();
      this.togglePanel();
      return;
    }
    if (!this.state.panelOpen) return;
    if (e.key === 'Escape') this.closePanel();
    if (e.key === 'Tab') this.handleTabNavigation(e);
  }

  handleTabNavigation(e) {
    if (!this.focusableElements.length) return;
    const first = this.focusableElements[0];
    const last = this.focusableElements[this.focusableElements.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  setupFocusableElements() {
    const sel = 'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])';
    this.focusableElements = Array.from(this.panel.querySelectorAll(sel)).filter((el) => el.offsetParent !== null);
  }
}
